
import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Mesh, Vector3, CatmullRomCurve3 } from 'three';
import { DecorationInfo } from '../types';

export const Bauble: React.FC<{ info: DecorationInfo }> = ({ info }) => {
  const meshRef = useRef<Mesh>(null);
  
  useFrame((state) => {
    if (!meshRef.current) return;
    meshRef.current.position.y = info.position[1] + Math.sin(state.clock.elapsedTime * 1.5 + info.id.length) * 0.03;
  });

  return (
    <group position={info.position}>
      <mesh ref={meshRef} castShadow>
        <sphereGeometry args={[info.size, 24, 24]} />
        <meshStandardMaterial 
          color={info.color} 
          metalness={0.9} 
          roughness={0.1} 
          emissive={info.color}
          emissiveIntensity={0.2}
        />
      </mesh>
      <pointLight color={info.color} intensity={0.4} distance={1.5} />
    </group>
  );
};

export const Star: React.FC<{ position: [number, number, number] }> = ({ position }) => {
  const meshRef = useRef<Mesh>(null);
  const lightRef = useRef<any>(null);

  useFrame((state) => {
    if (!meshRef.current) return;
    meshRef.current.rotation.y = state.clock.elapsedTime * 1.2;
    if (lightRef.current) {
        lightRef.current.intensity = 2 + Math.sin(state.clock.elapsedTime * 4) * 0.5;
    }
  });

  return (
    <group position={position}>
      <mesh ref={meshRef}>
        <octahedronGeometry args={[0.45, 0]} />
        <meshStandardMaterial 
          color="#ffca28" 
          emissive="#ffca28" 
          emissiveIntensity={4} 
          toneMapped={false}
        />
      </mesh>
      {/* Secondary glow mesh */}
      <mesh rotation={[Math.PI / 4, 0, Math.PI / 4]}>
        <octahedronGeometry args={[0.3, 0]} />
        <meshStandardMaterial color="#fff" emissive="#fff" emissiveIntensity={2} />
      </mesh>
      <pointLight ref={lightRef} color="#ffca28" intensity={3} distance={10} />
    </group>
  );
};

export const FairyLights: React.FC<{ levels: any[] }> = ({ levels }) => {
  const points = useMemo(() => {
    const p = [];
    for (let l = 0; l < levels.length; l++) {
      const level = levels[l];
      const count = 20;
      for (let i = 0; i <= count; i++) {
        const angle = (i / count) * Math.PI * 2;
        const radius = level.radius * 0.8;
        const y = level.pos + (i / count) * (level.height * 0.5);
        p.push(new Vector3(Math.cos(angle) * radius, y, Math.sin(angle) * radius));
      }
    }
    return p;
  }, [levels]);

  return (
    <group>
      {points.map((pos, i) => (
        i % 3 === 0 && (
          <mesh key={i} position={pos}>
            <sphereGeometry args={[0.02, 8, 8]} />
            <meshStandardMaterial 
              color={i % 2 === 0 ? "#fff" : "#ffeb3b"} 
              emissive={i % 2 === 0 ? "#fff" : "#ffeb3b"} 
              emissiveIntensity={2}
            />
          </mesh>
        )
      ))}
    </group>
  );
};
